// Piezas compartidas por las dos solapas de la nomina: badges de estado,
// selector de titular, campo con etiqueta y el copy de cada error. El copy
// vive aca y no en la logica: los tests de logica no comparan mensajes.
import { useEffect, useId, useState } from "react";
import type { Eligibility, RosterHolder, Subcontractor } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Badge } from "../components/ui/badge.js";
import { Failure } from "../components/ui/failure.js";
import { SkeletonRows } from "../components/ui/skeleton.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { cn } from "../components/ui/cn.js";
import { ineligibilityReasonLabel } from "../logic/documents.js";
import { FleetError, type FleetErrorKind, type FleetPort, type FleetSite } from "./port.js";
import type { RosterState } from "./useRoster.js";

export function EligibilityBadge({ eligibility }: { eligibility: Eligibility }): React.JSX.Element {
  if (eligibility.eligible) return <Badge variant="ok">Elegible</Badge>;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge variant="critico">No elegible</Badge>
      <ul className="m-0 list-none p-0 text-xs leading-snug text-texto-suave">
        {eligibility.reasons.map((reason, index) => (
          <li key={`${reason.code}-${String(index)}`}>{ineligibilityReasonLabel(reason)}</li>
        ))}
      </ul>
    </div>
  );
}

/** Estado en la planta: el bloqueo lo decide la planta, el proveedor solo lo ve. */
export function SiteStatusBadge({ active }: { active: boolean }): React.JSX.Element {
  return active ? <Badge variant="neutro">Habilitado</Badge> : <Badge variant="critico">Bloqueado por la planta</Badge>;
}

/**
 * Titular de la fila (PP-22): la API lo manda solo al proveedor y a UCORP.
 * Ausente no deberia pasar en el portal; si pasa, no se inventa.
 */
export function HolderCell({ holder }: { holder: RosterHolder | undefined }): React.JSX.Element {
  if (holder === undefined) return <span className="text-xs text-texto-suave">Sin dato</span>;
  return (
    <div className="flex flex-col">
      <span>{holder.brandName}</span>
      <span className="text-xs text-texto-suave">{holder.relation === "contractor" ? "Empresa madre" : "Tercerizada"}</span>
    </div>
  );
}

export function RosterBadge({ inRoster }: { inRoster: boolean }): React.JSX.Element {
  return inRoster ? <Badge variant="neutro">En nómina</Badge> : <Badge variant="neutro">De baja</Badge>;
}

/** Cargando, sin acceso y error del listado. El vacio lo pinta cada solapa con su copy. */
export function ListNotice<T>({ state, what, onRetry }: { state: RosterState<T>; what: string; onRetry?: () => void }): React.JSX.Element | null {
  if (state.estado === "cargando") return <SkeletonRows rows={4} label={`Consultando ${what}`} />;
  if (state.estado === "denegado") {
    return <Failure title="La cuenta no tiene acceso a la nómina de esta planta.">Salir e ingresar con una cuenta de operador de proveedor.</Failure>;
  }
  if (state.estado === "error") {
    return <Failure title={`No se pudo consultar ${what}. Reintentar en unos minutos.`} {...(onRetry === undefined ? {} : { onRetry })} />;
  }
  return null;
}

type Operation = "save" | "remove" | "reinstate";

const SAVE_COPY: Record<FleetErrorKind, string> = {
  access: "La cuenta no tiene permiso para cambiar la nómina de esta planta.",
  not_found: "No se encontró el registro. Puede haber cambiado: actualizar la lista.",
  conflict: "Ya hay otro registro activo con esa patente o ese código de móvil.",
  subcontract_missing: "La tercerizada elegida ya no está activa para la empresa. Elegir otro titular.",
  validation: "Hay datos que no se pueden guardar. Revisarlos e intentar de nuevo.",
  unavailable: "No se pudo guardar. Reintentar en unos minutos.",
};

const REMOVE_COPY: Record<FleetErrorKind, string> = {
  ...SAVE_COPY,
  conflict: "No se pudo dar de baja porque el registro cambió. Actualizar la lista.",
  unavailable: "No se pudo dar de baja. Reintentar en unos minutos.",
};

const REINSTATE_COPY: Record<FleetErrorKind, string> = {
  ...SAVE_COPY,
  conflict: "No se pudo reincorporar porque el registro cambió. Actualizar la lista.",
  subcontract_missing: "El titular ya no es una tercerizada activa de la empresa: no se puede reincorporar.",
  unavailable: "No se pudo reincorporar. Reintentar en unos minutos.",
};

const COPY: Record<Operation, Record<FleetErrorKind, string>> = {
  save: SAVE_COPY,
  remove: REMOVE_COPY,
  reinstate: REINSTATE_COPY,
};

export function errorCopy(error: unknown, operation: Operation): string {
  const kind: FleetErrorKind = error instanceof FleetError ? error.kind : "unavailable";
  return COPY[operation][kind];
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

export function Field({ label, hint, className, ...props }: FieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...(hint === undefined ? {} : { "aria-describedby": hintId })} {...props} />
      {hint !== undefined && (
        <p id={hintId} className="m-0 text-xs text-texto-suave">
          {hint}
        </p>
      )}
    </div>
  );
}

const SELECT_CLASS = cn(
  "desplegable h-10 w-full rounded-md border border-linea bg-hundida pl-3 pr-8",
  "font-sans text-sm text-texto",
  "focus:border-acero-claro focus:shadow-[0_0_0_2px_rgb(37_99_235/0.25)] focus:outline-none",
  "disabled:opacity-45",
);

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
}

export function SelectField({ label, hint, children, ...props }: SelectFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select id={id} className={SELECT_CLASS} {...(hint === undefined ? {} : { "aria-describedby": hintId })} {...props}>
        {children}
      </select>
      {hint !== undefined && (
        <p id={hintId} className="m-0 text-xs text-texto-suave">
          {hint}
        </p>
      )}
    </div>
  );
}

export type HoldersState =
  | { estado: "cargando" }
  | { estado: "error" }
  | { estado: "listo"; subcontractors: readonly Subcontractor[] };

/**
 * Tercerizadas activas de la empresa madre, para elegir titular en el alta
 * (PP-21). Si la lectura falla, el alta sigue posible a nombre de la empresa
 * madre y la pantalla lo dice.
 */
export function useHolders(port: FleetPort, session: PortalSession, site: FleetSite): HoldersState {
  const [state, setState] = useState<HoldersState>({ estado: "cargando" });
  const { siteId, providerId } = site;
  useEffect(() => {
    let current = true;
    setState({ estado: "cargando" });
    port
      .listSubcontractors(session, { siteId, providerId })
      .then((subcontractors) => {
        if (current) setState({ estado: "listo", subcontractors });
      })
      .catch(() => {
        if (current) setState({ estado: "error" });
      });
    return () => {
      current = false;
    };
  }, [port, session, siteId, providerId]);
  return state;
}

export function HolderSelect({
  holders,
  value,
  onChange,
}: {
  holders: HoldersState;
  value: string;
  onChange: (holderId: string) => void;
}): React.JSX.Element {
  const hint =
    holders.estado === "cargando"
      ? "Consultando las tercerizadas de la empresa..."
      : holders.estado === "error"
        ? "No se pudieron consultar las tercerizadas. El alta queda a nombre de la empresa madre."
        : holders.subcontractors.length === 0
          ? "La empresa no tiene tercerizadas activas: el alta queda a su nombre."
          : "La empresa madre o una de sus tercerizadas activas.";
  return (
    <SelectField
      label="Titular"
      hint={hint}
      value={value}
      disabled={holders.estado !== "listo" || holders.subcontractors.length === 0}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Empresa madre</option>
      {holders.estado === "listo" &&
        holders.subcontractors.map((sub) => (
          <option key={sub.id} value={sub.id}>
            {sub.brandName} (tercerizada)
          </option>
        ))}
    </SelectField>
  );
}

/**
 * Confirmacion en linea de la reincorporacion: vuelve a la nomina en todas las
 * plantas, pero no desbloquea ni la vuelve elegible por si sola.
 */
export function ReinstateConfirm({
  subject,
  busy,
  onConfirm,
  onCancel,
}: {
  subject: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <div role="group" aria-label={`Confirmar reincorporación de ${subject}`} className="flex max-w-[260px] flex-col gap-2">
      <p className="m-0 text-xs leading-normal text-texto-suave">
        Vuelve a la nómina en todas las plantas de la empresa. Un bloqueo de la planta o un documento vencido siguen valiendo.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="primario" disabled={busy} onClick={onConfirm}>
          {busy ? "Reincorporando..." : "Confirmar reincorporación"}
        </Button>
        <Button size="sm" variant="secundario" disabled={busy} onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

/** Confirmacion en linea de la baja: sin modal, y deja claro que no se borra nada. */
export function RemovalConfirm({
  subject,
  busy,
  onConfirm,
  onCancel,
}: {
  subject: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <div role="group" aria-label={`Confirmar baja de ${subject}`} className="flex max-w-[260px] flex-col gap-2">
      <p className="m-0 text-xs leading-normal text-texto-suave">
        Sale de la nómina en todas las plantas de la empresa. No se borra nada: el historial queda.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="primario" disabled={busy} onClick={onConfirm}>
          {busy ? "Dando de baja..." : "Confirmar baja"}
        </Button>
        <Button size="sm" variant="secundario" disabled={busy} onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
